import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AccountNavigationExtras, GotoRegistrationComplete, GotoResetPassword, GotoSetPassword } from '@platform-frontend/account';
import {
  GeneralStartMessageParams,
  GotoAcademicRegistration,
  GotoCommercialCheckout,
  GotoPurchaseInformation,
  GotoSubscriptionManagement,
  RefreshCheckoutMessageParams,
  RefreshSignInMessageParams,
  StartAcademicRegistrationPreference,
  getStartAcademicRegistrationPreferenceFromUserType
} from '@platform-frontend/checkout';
import { showAutostartError } from '@platform-frontend/platform';
import { Navigate } from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { Observable } from 'rxjs';
import {
  AutostartParams,
  CheckoutGeneralAutostartParams,
  CommercialCheckoutAutostartParams,
  CommercialCheckoutGeneralAutostartParams
} from './model/autostart-params';
import { WebCheckoutStartupRedirectionService } from './web-checkout-startup-redirection.service';

@Injectable({
  providedIn: 'root'
})
export class AutostartService {
  constructor(private store: Store, private webCheckoutStartupRedirectionService: WebCheckoutStartupRedirectionService) {}

  performAutostart(autostartRef: string): Observable<unknown> {
    let autostartParams: AutostartParams;
    if (typeof autostartRef !== 'string') {
      // TODO: This should not happen, but we have seen that on some systems with some browsers sometimes.
      // Hopefully this workaround fixes the issue in those rare cases.
      console.log('AutostartRef is not a string:', autostartRef);
      autostartParams = autostartRef;
    } else {
      autostartParams = JSON.parse(Buffer.from(autostartRef, 'base64').toString());
    }

    switch (autostartParams.type) {
      case 'confirmEmail': {
        const extras: AccountNavigationExtras<RefreshSignInMessageParams> = {
          redirect: autostartParams.returnPath ?? undefined,
          appStartParams: { vendorId: autostartParams.vendorId, ...this.getGeneralStartMessageParams(autostartParams) },
          queryParams: {
            errorType: autostartParams.errorType,
            context: autostartParams.context
          },
          autostartPage: true
        };
        return this.store.dispatch(new GotoRegistrationComplete(extras));
      }
      case 'confirmAcademicEmail':
        return this.store.dispatch(
          new Navigate(['/academic-confirmation'], {
            queryParams: {
              productId: autostartParams.productId,
              userType: autostartParams.userType,
              errorType: autostartParams.errorType,
              lang: autostartParams.locale,
              metadataId: autostartParams.metadataId
            },
            autostartPage: true
          })
        );
      case 'resetPassword':
      case 'setPassword': {
        const extras: AccountNavigationExtras<RefreshSignInMessageParams> = {
          redirect: autostartParams.returnPath ?? undefined,
          appStartParams: { vendorId: autostartParams.vendorId, ...this.getGeneralStartMessageParams(autostartParams) },
          queryParams: {
            restoreId: autostartParams.restoreId,
            email: autostartParams.email
          },
          autostartPage: true
        };
        return this.store.dispatch(autostartParams.type === 'resetPassword' ? new GotoResetPassword(extras) : new GotoSetPassword(extras));
      }
      case 'academicRegistration': {
        const start: StartAcademicRegistrationPreference | null = autostartParams.userType
          ? getStartAcademicRegistrationPreferenceFromUserType(autostartParams.userType)
          : null;
        return this.store.dispatch(
          new GotoAcademicRegistration(
            {
              productIdentifier: { productId: autostartParams.productId, environment: 'LIVE' },
              start,
              ...this.getGeneralStartMessageParams(autostartParams)
            },
            { autostartPage: true }
          )
        );
      }
      case 'commercialCheckout': {
        return this.store.dispatch(new GotoCommercialCheckout(this.getRefreshCheckoutMessageParams(autostartParams), { autostartPage: true }));
      }
      case 'subscriptionManagement':
        return this.store.dispatch(new GotoSubscriptionManagement(this.getRefreshCheckoutMessageParams(autostartParams), { autostartPage: true }));
      case 'purchaseInformation':
        return this.store.dispatch(new GotoPurchaseInformation(this.getRefreshCheckoutMessageParams(autostartParams), { autostartPage: true }));
      case 'signIn':
        return this.webCheckoutStartupRedirectionService.gotoSignInOrRegistrationComplete(
          navAction => this.store.dispatch(navAction),
          'MANUAL',
          { vendorId: autostartParams.vendorId, metadata: null, forceSignIn: false, ...this.getGeneralStartMessageParams(autostartParams) },
          { autostartPage: true }
        );
      case 'error':
        return showAutostartError(this.store, autostartParams.errorType, autostartParams.locale);
      default:
        return showAutostartError(this.store, 'UNKNOWN_PARAM_TYPE', null);
    }
  }

  private getRefreshCheckoutMessageParams(
    autostartParams: CommercialCheckoutGeneralAutostartParams | CommercialCheckoutAutostartParams
  ): RefreshCheckoutMessageParams {
    return {
      start: 'startCheckoutPreference' in autostartParams ? autostartParams.startCheckoutPreference ?? null : null,
      productIdentifier: autostartParams.productIdentifier,
      contractId: autostartParams.contractId,
      ...this.getGeneralStartMessageParams(autostartParams)
    };
  }

  private getGeneralStartMessageParams(autostartParams: CheckoutGeneralAutostartParams): GeneralStartMessageParams {
    return {
      locale: autostartParams.locale,
      metadataId: autostartParams.metadataId
    };
  }
}
